import { Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { _, TranslatePipe } from '@ngx-translate/core';
import { mgcIconDocument2Regular } from '@sb/mgc-icons/mgcIcon-document_2_regular.icon';
import { mgcIconFileUnknownRegular } from '@sb/mgc-icons/mgcIcon-file_unknown_regular.icon';
import { mgcIconLockRegular } from '@sb/mgc-icons/mgcIcon-lock_regular.icon';
import { mgcIconSearchRegular } from '@sb/mgc-icons/mgcIcon-search_regular.icon';
import { mgcIconTimeRegular } from '@sb/mgc-icons/mgcIcon-time_regular.icon';
import { mgcIconWarningRegular } from '@sb/mgc-icons/mgcIcon-warning_regular.icon';
import { BrandedLoaderComponent } from '@sb/ui/branded-loader';
import { ButtonComponent } from '@sb/ui/button';
import { EmptyStateComponent } from '@sb/ui/feedback';
import { IconComponent, IconRegistry, type Icon } from '@sb/ui/icon';
import { clsx } from 'clsx';
import { EMPTY, switchMap, take, timer } from 'rxjs';

import type { DetectionProblem } from './detection-state.helper';

const PHRASE_INTERVAL_MS = 4000;
const SCAN_PHRASES = [
  _('Reading document…'),
  _('Finding what changes per contract…'),
  _('Matching to your fields…'),
  _('Checking who signs…'),
];

interface ProblemCopy {
  icon: Icon;
  role: 'alert' | 'status';
  title: string;
  description: string;
  action: 'retry' | 'uploadAgain' | null;
}

const PROBLEMS: Record<DetectionProblem, ProblemCopy> = {
  error: {
    icon: 'warning-regular',
    role: 'alert',
    title: _('Your contract wasn’t checked'),
    description: _(
      'Something went wrong on our side. Your document is fine. Try again, or set up the template manually.',
    ),
    action: 'retry',
  },
  rate_limited: {
    icon: 'time-regular',
    role: 'alert',
    title: _('AI check limit reached'),
    description: _(
      'Your account has reached its limit of AI contract checks. You can set up this template manually instead.',
    ),
    action: 'retry',
  },
  unreadable: {
    icon: 'file-unknown-regular',
    role: 'alert',
    title: _('Your contract can’t be read'),
    description: _('Open the document in Word, save it as .docx, then upload it again.'),
    action: 'uploadAgain',
  },
  too_large: {
    icon: 'file-unknown-regular',
    role: 'alert',
    title: _('Your contract is too large'),
    description: _('Remove large images or extra pages in Word, save it as .docx, then upload it again.'),
    action: 'uploadAgain',
  },
  empty: {
    icon: 'search-regular',
    role: 'status',
    title: _('No fields found'),
    description: _(
      'We didn’t find anything in this contract that changes from person to person. Try another document, or set up the template manually.',
    ),
    action: 'uploadAgain',
  },
  forbidden: {
    icon: 'lock-regular',
    role: 'alert',
    title: _('You don’t have access'),
    description: _('Ask your account admin about access, or set up the template manually.'),
    action: null,
  },
};

/** What the builder shows until a detection resolves. */
@Component({
  selector: 'detection-progress',
  templateUrl: './detection-progress.component.html',
  imports: [BrandedLoaderComponent, ButtonComponent, EmptyStateComponent, IconComponent, TranslatePipe],
  host: { '[class]': 'hostClasses' },
})
export class DetectionProgressComponent {
  hostClasses = clsx('flex flex-col items-center justify-center p-lg');

  state = input.required<'pending' | DetectionProblem>();
  ready = input(false);
  fileName = input<string | null>();

  #phraseIndex = toSignal(
    toObservable(this.state).pipe(
      switchMap((state) =>
        state === 'pending' ? timer(0, PHRASE_INTERVAL_MS).pipe(take(SCAN_PHRASES.length)) : EMPTY,
      ),
    ),
    { initialValue: 0 },
  );

  problem = computed(() => {
    const state = this.state();

    return state === 'pending' ? null : PROBLEMS[state];
  });

  phrase = computed(() => (this.ready() ? _('Your contract is ready') : SCAN_PHRASES[this.#phraseIndex()]));

  retry = output();
  uploadAgain = output();
  setUpManually = output();

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconDocument2Regular,
      mgcIconFileUnknownRegular,
      mgcIconLockRegular,
      mgcIconSearchRegular,
      mgcIconTimeRegular,
      mgcIconWarningRegular,
    ]);
  }
}
